'use client'

import { useCallback, useEffect, useState } from 'react'
import { clsx } from 'clsx'
import CloseIcon from '../../component-library/Icons/CloseIcon'

export enum ModalMobileSize {
  'small' = 'small',
  'large' = 'large',
  'medium' = 'medium',
  'xsmall' = 'xsmall',
}

interface ModalProps {
  isOpen?: boolean
  onClose: () => void
  body?: React.ReactNode
  disabled?: boolean
  modalHeader?: string
  enableOverlay?: boolean
  modalSize?: ModalMobileSize
  enableScroll?: boolean
  removeModalHeader?: boolean
  hasHorizontalPadding?: boolean
}

const ModalMobile = ({
  isOpen,
  onClose,
  body,
  disabled,
  modalHeader,
  removeModalHeader = false,
  enableOverlay = true,
  modalSize = ModalMobileSize.small,
  enableScroll = false,
  hasHorizontalPadding = true,
}: ModalProps) => {
  const [showModal, setShowModal] = useState(isOpen)

  useEffect(() => {
    setShowModal(isOpen)
  }, [isOpen])

  const handleClose = useCallback(() => {
    if (disabled) {
      return
    }

    setShowModal(false)
    setTimeout(() => {
      onClose()
    }, 300)
  }, [onClose, disabled])

  if (!isOpen) {
    return null
  }

  const overlayClass = clsx({
    'bg-neutral-800/50': enableOverlay,
  })

  const enableScrollClass = clsx({
    'overflow-y-scroll': enableScroll,
  })

  const showModalClass = clsx({
    'translate-y-[25%]': showModal && modalSize === ModalMobileSize.large,
    'translate-y-[50%]': showModal && modalSize === ModalMobileSize.medium,
    'translate-y-[60%]': showModal && modalSize === ModalMobileSize.small,
    'translate-y-[75%]': showModal && modalSize === ModalMobileSize.xsmall,
    'translate-y-full': !showModal,
  })

  const showModalOpacityClass = clsx({
    'opacity-100': showModal,
    'opacity-0': !showModal,
  })

  const closeIconClass = clsx({
    'right-4': modalSize === ModalMobileSize.large,
    'right-9': modalSize === ModalMobileSize.small,
    'right-5': modalSize === ModalMobileSize.xsmall,
  })

  const modalPaddingClass = clsx({
    'p-4': modalSize === ModalMobileSize.large,
    'p-9': modalSize === ModalMobileSize.small,
    'p-11': modalSize === ModalMobileSize.xsmall,
  })

  const modalContainerClass = clsx({
    'h-2/3': modalSize === ModalMobileSize.large,
    'h-1/4': modalSize === ModalMobileSize.small || modalSize === ModalMobileSize.medium,
    'h-36': modalSize === ModalMobileSize.xsmall,
  })

  const modalHorizontalPaddingClass = clsx({
    'px-6': hasHorizontalPadding,
  })

  return (
    <div
      className={clsx(
        'fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-hidden',
        overlayClass
      )}
    >
      <div className="mx-auto my-6 h-full w-[480px]">
        <div
          className={clsx('translate h-full duration-300', showModalClass, showModalOpacityClass)}
        >
          <div className="bg-background-elevated h-full rounded-lg shadow-lg">
            {!removeModalHeader && (
              <div className={clsx('flex items-center justify-start rounded-t', modalPaddingClass)}>
                {modalHeader && <p className="text-left">{modalHeader}</p>}
                <div
                  className={clsx('text-accent-status-info absolute border-0 p-1', closeIconClass)}
                  onClick={handleClose}
                >
                  <CloseIcon />
                </div>
              </div>
            )}
            <div
              className={clsx(
                'flex-auto',
                enableScrollClass,
                modalContainerClass,
                modalHorizontalPaddingClass
              )}
            >
              {body}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModalMobile
